// Helper: format today's date as DD/MM/YYYY
function getTodayFormatted() {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

/**
 * Build the initial SDS template data block in the requested language.
 * Pulls every default string from the i18next dictionary so the report content
 * itself follows the language switch (ar ↔ en).
 *
 * @param {Function} t  i18next `t` function bound to the requested language.
 */
export function buildInitialSDSData(t) {
  // Tiny helper so we don't repeat the namespace prefix everywhere.
  const D = (k) => t(`sds.defaults.${k}`);

  return {
    header: {
      authorityName: t('sds.headerDefaults.authority'),
      authorityNameEn: t('sds.headerDefaults.authorityEn'),
      title: t('sds.headerDefaults.title'),
      subtitle: t('sds.headerDefaults.subtitle'),
    },
    facility: {
      name: '',
      activity: '',
      location: '',
    },
    chemicals: [
      {
        id: 1,
        name: 'Hydrex 1320',
        chemicalName: D('newChemicalChemName'),
        unNumber: D('newChemicalUn'),
        ghsClassification: D('newChemicalGhs'),
        ghsSymbols: [],
        usage: D('newChemicalUsage'),
        image: 'https://images.unsplash.com/photo-1663177207329-15541ee7951d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxjaGVtaWNhbCUyMGplcnJ5Y2FufGVufDB8fHx8MTc4MTYzNzAxN3ww&ixlib=rb-4.1.0&q=85&w=200&h=200&fit=crop',
        nfpa: { health: 1, fire: 0, reactivity: 0, special: '-' },
        risk: {
          material: 'Hydrex 1320',
          probability: D('probabilityLow'),
          severity: D('severityLow'),
          level: D('riskLow'),
          class: D('riskLow'),
        },
      },
    ],
    notes: D('notesDefault'),
    nfpaLegend: {
      title: t('sds.nfpa.legendTitle'),
      items: [
        { color: '#2563eb', label: t('sds.nfpa.legendHealth') },
        { color: '#dc2626', label: t('sds.nfpa.legendFire') },
        { color: '#facc15', label: t('sds.nfpa.legendReactivity') },
        { color: '#ffffff', label: t('sds.nfpa.legendSpecial') },
      ],
    },
    safeHandling: {
      title: D('safeHandlingTitle'),
      items: [D('safeHandling1'), D('safeHandling2'), D('safeHandling3'), D('safeHandling4'), D('safeHandling5'), D('safeHandling6')],
    },
    ppe: {
      title: D('ppeTitle'),
      items: [D('ppe1'), D('ppe2'), D('ppe3'), D('ppe4'), D('ppe5')],
    },
    safeStorage: {
      title: D('safeStorageTitle'),
      items: [D('safeStorage1'), D('safeStorage2'), D('safeStorage3'), D('safeStorage4'), D('safeStorage5')],
    },
    firstAid: {
      title: D('firstAidTitle'),
      items: [
        { label: D('firstAidInhaleLabel'), text: D('firstAidInhaleText') },
        { label: D('firstAidSkinLabel'), text: D('firstAidSkinText') },
        { label: D('firstAidEyeLabel'), text: D('firstAidEyeText') },
        { label: D('firstAidSwallowLabel'), text: D('firstAidSwallowText') },
      ],
    },
    spillProcedures: {
      title: D('spillTitle'),
      items: [D('spill1'), D('spill2'), D('spill3'), D('spill4'), D('spill5')],
    },
    hypotheticalScenario: {
      title: D('scenarioTitle'),
      incident: D('scenarioIncident'),
      actionsTitle: D('scenarioActionsTitle'),
      actions: [D('scenarioAction1'), D('scenarioAction2'), D('scenarioAction3'), D('scenarioAction4'), D('scenarioAction5'), D('scenarioAction6')],
    },
    emergencyPlan: {
      title: D('emergencyPlanTitle'),
      items: [D('emergencyPlan1'), D('emergencyPlan2'), D('emergencyPlan3'), D('emergencyPlan4'), D('emergencyPlan5'), D('emergencyPlan6')],
    },
    training: {
      title: D('trainingTitle'),
      items: [D('training1'), D('training2'), D('training3'), D('training4')],
    },
    safetyCulture: {
      title: D('safetyCultureTitle'),
      text: D('safetyCultureText'),
      motto: D('safetyCultureMotto'),
    },
    emergencyNumbers: {
      title: D('emergencyTitle'),
      unifiedNumber: '1920',
      departments: [D('emergencyDept1'), D('emergencyDept2'), D('emergencyDept3')],
    },
    footer: {
      preparedBy: D('footerPreparedBy'),
      supervisor: D('footerSupervisor'),
      nextReview: D('footerNextReview'),
      reviewNumber: D('footerReviewNumber'),
      issueNumber: D('footerIssueNumber'),
      date: getTodayFormatted(),
      notice: D('footerNotice'),
    },
  };
}

// Backwards-compat export: a stable Arabic seed used as fallback when no i18n context
// is available (e.g. lightweight tooling that imports the file without React).
export const initialSDSData = {
  header: {
    authorityName: 'الهيئة السعودية للمدن الصناعية ومناطق التقنية',
    authorityNameEn: 'Saudi Authority for Industrial Cities and Technology Zones',
    title: 'تصنيف المواد الكيميائية بالمصنع',
    subtitle: 'بطاقات بيانات وتعليمات السلامة (SDS)',
  },
  facility: { name: '', activity: '', location: '' },
  chemicals: [],
  notes: 'ملاحظة: أرقام UN حسب المعلومات المتاحة. يُرجى الرجوع إلى SDS الخاصة بكل منتج.',
  nfpaLegend: {
    title: 'مفتاح الألوان (NFPA)',
    items: [
      { color: '#2563eb', label: 'الصحة (أزرق)' },
      { color: '#dc2626', label: 'الاشتعال (أحمر)' },
      { color: '#facc15', label: 'التفاعل (أصفر)' },
      { color: '#ffffff', label: 'المخاطر الخاصة (أبيض)' },
    ],
  },
  safeHandling: { title: 'طرق التعامل الآمن', items: [] },
  ppe: { title: 'معدات الوقاية الشخصية (PPE)', items: [] },
  safeStorage: { title: 'التخزين الآمن', items: [] },
  firstAid: { title: 'الإسعافات الأولية', items: [] },
  spillProcedures: { title: 'إجراءات التسرب والانسكاب', items: [] },
  hypotheticalScenario: { title: 'سيناريو افتراضي', incident: '', actionsTitle: 'الإجراءات المتخذة:', actions: [] },
  emergencyPlan: { title: 'خطة الاستجابة للطوارئ', items: [] },
  training: { title: 'التدريب والمراجعة', items: [] },
  safetyCulture: { title: 'ثقافة السلامة', text: '', motto: 'السلامة مسؤوليتنا جميعاً' },
  emergencyNumbers: { title: 'أرقام الطوارئ', unifiedNumber: '1920', departments: ['إطفاء مدن', 'الإسعاف', 'الأمن الصناعي'] },
  footer: {
    preparedBy: 'إعداد: إدارة السلامة والصحة المهنية',
    supervisor: 'الجهة المشرفة: إدارة المدينة الصناعية الثانية بجدة',
    nextReview: 'المراجعة القادمة: بعد 12 شهراً',
    reviewNumber: 'رقم المراجعة: 0',
    issueNumber: 'رقم الإصدار: 1.0',
    date: getTodayFormatted(),
    notice: 'تم إعداد هذا التقرير بالاعتماد على بطاقات بيانات السلامة (SDS) الخاصة بالمواد الكيميائية المستخدمة بالموقع.',
  },
};

export const STORAGE_KEY = 'sds_template_data_v1';
